import { StyledIconByName } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/StyledIconByName";
import { Core, Decorative } from "@faire/design-tokens";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Modal } from "@faire/web/slate/deprecated/Modal";
import { Column, Flex } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import type { JSX } from "react";
import styled from "styled-components";

export const EcoFriendlyInfoAccordionDetailsModal: React.FC<{
  ecoFriendlyInfoSection: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection;
  onClose: () => void;
  isOpen: boolean;
  learnMoreCTA?: JSX.Element;
}> = ({ ecoFriendlyInfoSection, onClose, isOpen, learnMoreCTA }) => {
  const { isMobile } = useViewport();

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      height="auto"
      align="flex-start"
      padding={getSpacing("3x", 0, "3x", "3x")}
      contentStyle={
        isMobile
          ? { maxHeight: "100%", maxWidth: "100%" }
          : { maxHeight: "712px", maxWidth: "383px", display: "block" }
      }
      closeIconProps={{ margin: "20px 20px 0 0" }}
    >
      <Column style={{ marginTop: getSpacing("5x") }}>
        <ScrollableContainer>
          <Spacer height="3x" />
          <ModalTitle variant="pageHeaderSerifRegular">
            <StrictLocalMsg
              id="bS1KMV"
              defaultMessage="Eco-friendly information"
              description={{
                text: "Title for the Eco-friendly information modal.",
                img: "https://cdn.faire.com/fastly/3c43577445f61111cfe2e63ed9d3e84af1c0487a2fe5be0493bd603be43eb6a3.png",
              }}
            />
          </ModalTitle>
          <ModalSubHeader variant="labelSansMedium">
            <StrictLocalMsg
              id="n/F3/B"
              defaultMessage="Self-reported by the brand"
              description={{
                text: "The eco-friendly information is claimed by the brand itself.",
                img: "https://cdn.faire.com/fastly/3c43577445f61111cfe2e63ed9d3e84af1c0487a2fe5be0493bd603be43eb6a3.png",
              }}
            />
          </ModalSubHeader>
          {ecoFriendlyInfoSection.modal_information_sections.map(
            attributeDetailsBlock
          )}
        </ScrollableContainer>
      </Column>
      <Spacer height="4x" />
      <StickyContent>
        <Button variant="secondary" onClick={onClose}>
          <StrictLocalMsg
            id="+VN6Dz"
            defaultMessage="Close"
            description={{
              text: "The customer clicks this button to close the details modal.",
            }}
          />
        </Button>
        {learnMoreCTA}
      </StickyContent>
    </Modal>
  );
};

const attributeDetailsBlock = (
  ecoFriendlyAttributeDetail: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IModalInformationSection
) => {
  if (!ecoFriendlyAttributeDetail.header_text) {
    return null;
  }

  return (
    <div
      key={`eco-friendly-attribute-modal-${ecoFriendlyAttributeDetail.header_text}`}
    >
      <Spacer height="4x" />
      <IconImg>
        <StyledIconByName
          name={ecoFriendlyAttributeDetail.slate_icon?.type}
          fontSize="20px"
          color={Decorative.shade.orange}
          strokeWidth={1.2}
        />
      </IconImg>
      <Spacer height="1x" />
      <Typography variant="subheadingSansMedium">
        {ecoFriendlyAttributeDetail.header_text}
      </Typography>
      {ecoFriendlyAttributeDetail.value_details.map((attributeValue) =>
        attributeValueAndDescription(
          ecoFriendlyAttributeDetail.header_text ?? "",
          attributeValue
        )
      )}
    </div>
  );
};

const attributeValueAndDescription = (
  attributeName: string,
  attributeValue: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IModalInformationSection.IAttributeValueDetail
) => (
  <div
    key={`eco-friendly-attribute-modal-value-${attributeName}-${attributeValue.name}`}
  >
    <Spacer height="1x" />
    <Typography variant="paragraphSansMedium">{attributeValue.name}</Typography>
    <Typography variant="paragraphSansRegular">
      {attributeValue.description}
    </Typography>
  </div>
);

const ModalTitle = styled(Typography)`
  align-self: flex-start;
`;

const ModalSubHeader = styled(Typography).attrs({
  color: Core.text.subdued,
})`
  font-weight: 200;
`;

const IconImg = styled(Flex).attrs({ align: "center", justify: "center" })`
  width: ${getSpacing("5x")};
  height: ${getSpacing("5x")};
  border-radius: 100%;
  background-color: ${Decorative.tint.orange};
`;

const StickyContent = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})`
  padding-right: ${getSpacing("3x")};

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const ScrollableContainer = styled(Column)`
  overflow: auto;
  max-height: 544px;
  padding-right: ${getSpacing("3x")};

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3.5px;
    background-color: ${Core.icon.subdued};
    -webkit-box-shadow: 0 0 1px ${Core.surface.inverse};
  }

  ${responsiveMediaQueries.mobile} {
    height: calc(100vh - 204px);
    max-height: none;
  }
`;
